import React, { useEffect, useState } from 'react';
import { LayoutSideNavigation, Page, UserNav } from '../../components';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './ShippingDetailsPage.module.css';
import { showCreateListingLinkForUser, showPaymentDetailsForUser } from '../../util/userHelpers';
import { currentUserSelector } from '../../ducks/user.duck';
import { useConfiguration } from '../../context/configurationContext';
import { createShippoAddress, selectShippoAddressStatus } from '../../ducks/shippo.duck';
import ShippingDetailsForm from './ShippingDetailsForm/ShippingDetailsForm';
import getCountryCodes from '../../translations/countryCodes';
import {
  getShippingDetails,
  getShippingDimensions,
  getShippingPreference,
  getUserCountry,
  getUserEmail,
  getUserFullName,
  getUserPhoneNumber,
  isUserInEUCountry,
} from '../../util/dataExtractor';
import { distanceUnitOptions, weightUnitEnum } from '../../config/configExtras';
import { ShippingPreference } from '../../util/enums';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { updateCountryInListing } from '../../ducks/global.duck';
import { listingsSelector } from './ShippingDetailsPage.duck';
import { isArrayLength } from '../../util/genericHelpers';
import ShippingPriceInputFieldModal from './ShippingPriceInputFieldModal';
const ShippingDetailsPage = () => {
  const dispatch = useDispatch();
  const config = useConfiguration();
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const currentUser = useSelector(currentUserSelector);
  const listings = useSelector(listingsSelector);
  const { saveShippoAddressInProgress, saveShippoAddressError, shippoAddressSaved } = useSelector(
    selectShippoAddressStatus
  );
  const intl = useIntl();
  const schemaTitle = intl.formatMessage({
    id: 'ShippingDetailsPage.title',
  });
  const [inProgress, setInProgress] = useState(false);
  const [showShippingPriceModal, setShowShippingPriceModal] = useState(false);

  const showManageListingsLink = showCreateListingLinkForUser(config, currentUser);
  const { showPayoutDetails, showPaymentMethods } = showPaymentDetailsForUser(config, currentUser);
  const accountSettingsNavProps = {
    currentPage: 'ShippingDetailsPage',
    showPaymentMethods,
    showPayoutDetails,
  };
  const shippingAddress = getShippingDetails(currentUser);
  const userCountry = getUserCountry(currentUser);
  const isEUCountry = isUserInEUCountry(currentUser);

  const shippingPreference = getShippingPreference(currentUser);
  const shippingDimensions = getShippingDimensions(currentUser);
  const countryCodes = getCountryCodes(config.localization.locale);

  // Priority: shippingAddress.country → userCountry → fallback to null
  const preferredCountryCode = shippingAddress?.country || userCountry;

  // Find the full country object (with translated name, etc.)
  const savedCountry = preferredCountryCode
    ? countryCodes.find(c => c.code === preferredCountryCode)
    : null;

  const currentUserName = getUserFullName(currentUser);
  const currentUserEmail = getUserEmail(currentUser);
  const currentUserPhoneNumber = getUserPhoneNumber(currentUser);
  const savedWeightUnit = weightUnitEnum.find(c => c.value === shippingDimensions?.unit);
  const distanceUnit = distanceUnitOptions.find(c => c.value === shippingDimensions?.distanceUnit);
  const currentUserId = currentUser?.id?.uuid;
  const listingsMissingShippingPrice = isArrayLength(listings)
    ? listings.filter(listing => !listing?.attributes?.publicData?.shippingPriceInSubunitsOneItem)
    : [];

  useEffect(() => {
    if (isArrayLength(listingsMissingShippingPrice)) {
      setShowShippingPriceModal(true);
    }
  }, [listingsMissingShippingPrice.length]);
  const initialValues = {
    ...shippingAddress,
    country: savedCountry,
    weight: shippingDimensions?.weight,
    length: shippingDimensions?.length,
    width: shippingDimensions?.width,
    height: shippingDimensions?.height,
    massUnit: savedWeightUnit,
    distanceUnit,
    phone: currentUserPhoneNumber,
    shippingPreference: ShippingPreference.PLATFORM,
  };

  const handleSubmit = async values => {
    setInProgress(true);

    try {
      const { shippingPreference } = values || {};
      // Case 1: User selects "Own" shipping → just update profile (no address creation)
      if (shippingPreference === ShippingPreference.OWN) {
        dispatch(
          updateProfile({
            protectedData: {
              shippingPreference,
              shippingAddress: null,
            },
            publicData: {
              shippingPreference,
              addressObjectId: null,
              shippingDimensions: null,
              isEUCountry,
            },
          })
        );

        // Update country in user's listings/profile if needed
        await dispatch(
          updateCountryInListing({
            currentUserId,
            currentCountryValue: userCountry,
            previousCountryValue: null, // optional but recommended
          })
        );
        setInProgress(false);
        return; // Exit early
      }

      const shippingDetails = {
        ...values,
        shippingPreference,
        email: currentUserEmail,
        name: currentUserName,
        currentUserId,
        country: values?.country?.code,
        unit: values?.massUnit?.value,
        distanceUnit: values?.distanceUnit?.value,
      };

      await dispatch(createShippoAddress(shippingDetails));
      setInProgress(false);
    } catch (error) {
      setInProgress(false);
    }
  };
  return (
    <Page scrollingDisabled={scrollingDisabled} title={schemaTitle}>
      <LayoutSideNavigation
        topbar={
          <>
            <TopbarContainer
              desktopClassName={css.desktopTopbar}
              mobileClassName={css.mobileTopbar}
            />
            <UserNav
              currentPage="ShippingDetailsPage"
              showManageListingsLink={showManageListingsLink}
            />
          </>
        }
        footer={<FooterContainer />}
        sideNav={null}
        accountSettingsNavProps={accountSettingsNavProps}
        useAccountSettingsNav
      >
        <div className={css.content}>
          <ShippingDetailsForm
            className={css.form}
            hideTitle
            initialValues={initialValues}
            currentUser={currentUser}
            onSubmit={handleSubmit}
            inProgress={inProgress || saveShippoAddressInProgress}
            ready={shippoAddressSaved}
            updateProfileError={saveShippoAddressError}
          />
          {/* <ShippingPriceInputFieldModal
            isOpen={showShippingPriceModal}
            onClose={() => setShowShippingPriceModal(false)}
            listingsMissingShippingPrice={listingsMissingShippingPrice}
            intl={intl}
            config={config}
          /> */}
        </div>
      </LayoutSideNavigation>
    </Page>
  );
};
export default ShippingDetailsPage;
